<!-- Email Report Modal -->
<div class="modal-backdrop fade show" *ngIf="isOpen" (click)="close()"></div>
<div
  class="modal fade show d-block email-report-modal"
  tabindex="-1"
  role="dialog"
  aria-modal="true"
  aria-labelledby="emailReportModalTitle"
  *ngIf="isOpen"
  (click)="onBackdropClick($event)"
>
  <!-- modal-dialog-scrollable keeps the header and footer pinned and scrolls the body instead
       of growing the dialog past the viewport. Without it a short window cuts off the TOP of
       the modal, which is unreachable — you cannot scroll up to content above the viewport. -->
  <div
    class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
    (click)="$event.stopPropagation()"
  >
    <div class="modal-content" #dialogRoot>
      <div class="modal-header">
        <h5 class="modal-title" id="emailReportModalTitle">
          <i class="bi bi-envelope me-2"></i>Send Report to Agency
        </h5>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          (click)="close()"
          [disabled]="isProcessing()"
        ></button>
      </div>

      <div class="modal-body" *ngIf="report">
        <!-- What is being sent -->
        <div class="report-summary mb-4">
          <strong>{{ reportTitle }}</strong>
          <span class="text-muted ms-2">{{ reportSubtitle }}</span>
        </div>

        <p class="text-muted small mb-4">
          ADN prepares the message — you send it from your own email app. Nothing is emailed from here.
        </p>

        <!-- ============ STEP 1 — RECIPIENT ============ -->
        <section class="report-step" aria-labelledby="emailReportStep1">
          <h6 class="report-step-title" id="emailReportStep1">
            <span class="report-step-number">1</span> Recipient
          </h6>

          <div class="recipient-options" role="group" aria-labelledby="emailReportStep1">
            <button
              *ngFor="let agency of agencyOptions()"
              type="button"
              class="btn recipient-btn text-start"
              [class.btn-primary]="recipientChoice() === agency.id"
              [class.btn-outline-secondary]="recipientChoice() !== agency.id"
              [attr.aria-pressed]="recipientChoice() === agency.id"
              [disabled]="isProcessing()"
              (click)="selectRecipient(agency.id)"
            >
              <span class="d-block fw-medium">{{ agency.name }}</span>
              <small class="d-block recipient-btn-sub">{{ agency.description }}</small>
            </button>

            <button
              type="button"
              class="btn recipient-btn text-start"
              [class.btn-primary]="recipientChoice() === otherRecipientId"
              [class.btn-outline-secondary]="recipientChoice() !== otherRecipientId"
              [attr.aria-pressed]="recipientChoice() === otherRecipientId"
              [disabled]="isProcessing()"
              (click)="selectRecipient(otherRecipientId)"
            >
              <span class="d-block fw-medium">Other recipient…</span>
              <small class="d-block recipient-btn-sub">Enter the address yourself</small>
            </button>
          </div>

          <!-- Address still pending confirmation: say so, never render an empty string as real -->
          <div *ngIf="selectedAgencyPending()" class="alert alert-warning small mt-3 mb-0">
            <i class="bi bi-exclamation-triangle me-2"></i>
            <strong>Address pending.</strong>
            This agency's recipient address hasn't been confirmed yet. Enter the address from your
            operating exemption / certificate conditions below.
          </div>

          <!-- Free-text recipient (Other, or a preset whose address is pending) -->
          <div class="mt-3" *ngIf="needsCustomRecipient()">
            <label class="form-label fw-medium" for="emailReportRecipient">Recipient email</label>
            <input
              type="email"
              class="form-control"
              [class.is-invalid]="customEmail && !isValidEmail(customEmail)"
              id="emailReportRecipient"
              name="emailReportRecipient"
              [(ngModel)]="customEmail"
              [disabled]="isProcessing()"
              [placeholder]="recipientPlaceholder"
            >
            <div *ngIf="customEmail && !isValidEmail(customEmail)" class="invalid-feedback">
              Please enter a valid email address.
            </div>
          </div>

          <!-- Resolved address + its own copy action (webmail needs address and body separately) -->
          <div class="recipient-resolved mt-3" *ngIf="hasValidRecipient()">
            <span class="recipient-resolved-address text-truncate">{{ recipientAddress() }}</span>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary flex-shrink-0"
              (click)="copyRecipient()"
              [disabled]="isProcessing()"
            >
              <i class="bi bi-clipboard me-1"></i>Copy
            </button>
          </div>
        </section>

        <!-- ============ STEP 2 — DOWNLOAD ============ -->
        <section class="report-step" aria-labelledby="emailReportStep2">
          <h6 class="report-step-title" id="emailReportStep2">
            <span class="report-step-number">2</span> Download the report
          </h6>

          <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
            <p class="text-muted small mb-0">
              You'll attach this to the email yourself — email links can't carry the file for you.
            </p>
            <button
              type="button"
              class="btn btn-outline-primary flex-shrink-0"
              (click)="requestDownload()"
              [disabled]="isProcessing()"
            >
              <i class="bi bi-download me-2"></i>Download PDF
            </button>
          </div>

          <div class="text-success small mt-2" *ngIf="hasDownloaded()">
            <i class="bi bi-check-circle me-1"></i>PDF requested — check your downloads, then attach it to the email.
          </div>
        </section>

        <!-- ============ STEP 3 — YOUR MESSAGE ============ -->
        <section class="report-step" aria-labelledby="emailReportStep3">
          <h6 class="report-step-title" id="emailReportStep3">
            <span class="report-step-number">3</span> Your message
            <small class="text-muted fw-normal ms-2">(editable)</small>
          </h6>

          <!-- State filings vary by state; the template is a starting point, not a finished filing -->
          <div *ngIf="reportKind === 'state'" class="alert alert-info small">
            <i class="bi bi-info-circle me-2"></i>{{ stateReviewNote }}
          </div>

          <div class="mb-3">
            <label class="form-label fw-medium" for="emailReportSubject">Subject</label>
            <div class="input-group">
              <input
                type="text"
                class="form-control"
                id="emailReportSubject"
                name="emailReportSubject"
                [(ngModel)]="subject"
                (input)="onDraftEdited()"
                [disabled]="isProcessing()"
              >
              <button
                type="button"
                class="btn btn-outline-secondary"
                title="Copy subject"
                aria-label="Copy subject"
                (click)="copySubject()"
                [disabled]="isProcessing()"
              >
                <i class="bi bi-clipboard"></i>
              </button>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-medium" for="emailReportBody">Message</label>
            <textarea appCharCounter
              class="form-control draft-body"
              id="emailReportBody"
              name="emailReportBody"
              rows="14"
              [(ngModel)]="body"
              (input)="onDraftEdited()"
              [disabled]="isProcessing()"
            ></textarea>
            <div class="form-text" *ngIf="isPreparingDraft()">
              <span class="spinner-border spinner-border-sm me-1"></span>
              Filling in your organization and certificate details…
            </div>
          </div>

          <!-- Send actions. De-emphasized until the PDF has been downloaded this session —
               ordered, never trapped: both stay clickable. -->
          <div class="draft-actions" [class.draft-actions-muted]="!hasDownloaded()">
            <button
              type="button"
              class="btn"
              [class.btn-primary]="hasDownloaded()"
              [class.btn-outline-primary]="!hasDownloaded()"
              (click)="copyMessage()"
              [disabled]="isProcessing()"
            >
              <i class="bi bi-clipboard-check me-2"></i>Copy Message
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              *ngIf="isMailtoWithinLimit()"
              (click)="openInEmailApp()"
              [disabled]="!canOpenInEmailApp()"
            >
              <i class="bi bi-box-arrow-up-right me-2"></i>Open in Email App
            </button>
          </div>

          <div *ngIf="!isMailtoWithinLimit()" class="alert alert-warning small mt-3 mb-0">
            <i class="bi bi-exclamation-triangle me-2"></i>
            This message is too long for the one-click email shortcut — some email apps would cut
            it off without warning. Use <strong>Copy Message</strong> instead, or shorten the message.
          </div>

          <div *ngIf="mailClientAttempted()" class="alert alert-info small mt-3 mb-0">
            <i class="bi bi-info-circle me-2"></i>
            Nothing happened? This computer may not have an email app set up to handle
            <code>mailto:</code> links — that's common when you use webmail. Use
            <strong>Copy Message</strong> above and paste it into Gmail, Outlook on the web, or
            whatever you use.
          </div>

          <p class="text-muted small mt-3 mb-0">
            No email app? Copy the message and paste it into Gmail, Outlook on the web, or whatever you use.
          </p>
        </section>

        <!-- Submission error -->
        <div *ngIf="errorMessage()" class="alert alert-danger small mt-4 mb-0">
          <i class="bi bi-exclamation-circle me-1"></i>
          {{ errorMessage() }}
        </div>
      </div>

      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-outline-secondary"
          (click)="close()"
          [disabled]="isProcessing()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="markAsSubmitted()"
          [disabled]="!canMarkSubmitted()"
        >
          <ng-container *ngIf="!isProcessing()">
            <i class="bi bi-check2-circle me-2"></i>Mark as Submitted
          </ng-container>
          <ng-container *ngIf="isProcessing()">
            <span class="spinner-border spinner-border-sm me-2"></span>Saving...
          </ng-container>
        </button>
      </div>
    </div>
  </div>
</div>
